import './App.scss';
import './assets/css/icofont.min.css';
import './assets/css/MainPage.css';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import ConfigProvider from 'antd/es/config-provider';
import loadable from '@loadable/component';

import { adminRoutes, consulatntRoutes, clientRoutes } from './routing';
import { useHideLoader } from './hooks/useHideLoader';
import PrivateRoutesAdmin from './utils/PrivateRouteAdmin';
import PrivateRoutesCounsultant from './utils/PrivateRouteCounsultant';
import ConsultantLayout from './layout/consultant/Consultant';
import AdminLayout from './layout/admin/Admin';
import ClientLayout from './layout/client/Client';
import PrivateRoutesClient from './utils/PrivateRouteClient';
import { useSelector } from 'react-redux';
import { AppState } from './redux/store';
import { useEffect } from 'react';
import { AdminApi } from './api/api';
import { openNotificationWithIcon } from './pages/components/Toast';
import { useDispatch } from 'react-redux';
import { setWebSettingData } from './redux/webSetteing/actions';
import i18n from './i18n';
import { AnyAction } from 'redux';
import { LanguageProvider } from './components/LanguageContext';

const ScrollToTop = loadable(() => import('./pages/components/ScrollToTop'));
const MainPage = loadable(() => import('./pages/Website/MainPage/MainPage'));
const FilterPage = loadable(() => import('./pages/Website/FilterPage/FilterPage'));
const ConsultantSingleProfilePage = loadable(
  () => import('./pages/Website/FilterPage/MyProfile/ConsultantMyProfile')
);
const AboutPage = loadable(() => import('./pages/Website/About/AboutPage'));
const LifestyleProducts = loadable(
  () => import('./pages/Website/Lifestyle&Products/LifestyleProducts')
);
const TermsConditions = loadable(() => import('./pages/Website/Terms&Privacy/TermsConditions'));
const PrivacyPolicy = loadable(() => import('./pages/Website/Terms&Privacy/PrivacyPolicy'));
const TraditionalMedicinePage = loadable(
  () => import('./pages/Website/Traditional Medicine/TraditionalMedicinePage')
);
const SDG = loadable(() => import('./pages/Website/SDG/SDG'));
const Booking = loadable(() => import('./pages/dashboards/ClientDashboards/Booking/Booking'));
const Room = loadable(() => import('./pages/components/Room/Room'));
const NotFound = loadable(() => import('./pages/sessions/404'));
const ContactUs = loadable(() => import('./pages/Website/ContactUs/ContactUs'));

// Sign-in/Sign-up pages
const SignInLanding = loadable(() => import('./pages/sessions/Sign-in'));
const ClientSignIn = loadable(() => import('./pages/sessions/ClientSignInn'));
const ConsultantSignIn = loadable(() => import('./pages/sessions/ConsultantSign-in'));
const AdminSignIn = loadable(() => import('./pages/sessions/AdminSign-in'));
const ClientSignUp = loadable(() => import('./pages/sessions/Client-Sign-up'));
const ConsultantSignUp = loadable(() => import('./pages/sessions/ConsultantSign-up'));
const ClientForgetPassword = loadable(() => import('./pages/sessions/Client/ForgetPasswordPage'));
const ConsultantForgetPassword = loadable(() => import('./pages/sessions/Consultant/ForgetPasswordPage'));
const AdminForgetPassword = loadable(() => import('./pages/sessions/Admin/ForgetPasswordPage'));

// Language URL Synchronizer
const LanguageURLSync = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type: currentLanguage } = useSelector((state: AppState) => state.language);

  useEffect(() => {
    // Only sync if we're on a language-prefixed route and language changed
    if (location.pathname !== '/' && currentLanguage) {
      const currentLangPrefix = location.pathname.startsWith('/fr') ? 'fr' : 'en';
      const targetLangPrefix = currentLanguage;

      if (currentLangPrefix !== targetLangPrefix) {
        // Replace language prefix in current path
        const newPath = location.pathname.replace(/^\/(en|fr)/, `/${targetLangPrefix}`);
        navigate(newPath, { replace: true });
      }
    }
  }, [currentLanguage, location.pathname, navigate]);

  return null; // This component doesn't render anything
};

const App = () => {
  const dispatch = useDispatch();
  const state = useSelector((state: AppState) => state.consultant.Token);
  const webSettings = useSelector((state: AppState) => state.webSettings);
  const { type } = useSelector((state: AppState) => state.language);

  useEffect(() => {
    i18n.changeLanguage(type);
  }, [type]);
  useEffect(() => {
    let icons = document.querySelectorAll('link[type="image/jpg"]');
    icons.forEach((row: any) => (row.href = webSettings?.fab_icon));
    if (icons.length === 0) {
      const favicon: any = document.createElement('link');
      favicon.href = webSettings?.fab_icon;
      document.head.appendChild(favicon);
    }
  }, [webSettings]);

  useEffect(() => {
    (() => {
      AdminApi.getWebSetting('web-setting/get-record', state)
        .then(async (res) => {
          const action: AnyAction = setWebSettingData(res);
          dispatch(action);
        })
        .catch((err) => {
          openNotificationWithIcon({
            type: 'error',
            message: err.response?.data?.message || err.message
          });
        });
    })();
  }, [state, dispatch]);

  useHideLoader();

  // Centralized, smart multilingual routes generation
  const LANGS: Array<'en' | 'fr'> = ['en', 'fr'];

  const commonContentRoutes = [
    { path: 'about-us', element: <AboutPage /> },
    { path: 'alternative-medicine', element: <TraditionalMedicinePage /> },
    { path: 'sdg-media', element: <SDG /> },
    { path: 'our-experts', element: <FilterPage /> },
    { path: 'our-experts/:search', element: <FilterPage /> },
    { path: 'our-experts/details/:id', element: <ConsultantSingleProfilePage /> },
    { path: 'lifestyle-product', element: <LifestyleProducts /> },
    { path: 'terms-conditions', element: <TermsConditions /> },
    { path: 'privacy-policy', element: <PrivacyPolicy /> },
    { path: 'contact-us', element: <ContactUs /> },
    { path: 'traditional-medicine', element: <TraditionalMedicinePage /> },
    { path: 'chat-room/:roomid', element: <Room /> }
  ];

  const authRoutes = [
    { path: 'public/sign-in', element: <SignInLanding /> },
    { path: 'public/client/sign-in', element: <ClientSignIn /> },
    { path: 'public/consultant/sign-in', element: <ConsultantSignIn /> },
    { path: 'public/admin/sign-in', element: <AdminSignIn /> },
    { path: 'public/client/sign-up', element: <ClientSignUp /> },
    { path: 'public/consultant/sign-up', element: <ConsultantSignUp /> },
    { path: 'public/client/forget-password', element: <ClientForgetPassword /> },
    { path: 'public/consultant/forget-password', element: <ConsultantForgetPassword /> },
    { path: 'public/admin/forget-password', element: <AdminForgetPassword /> }
  ];

  return (
    <LanguageProvider>
      <LanguageURLSync />
      <ScrollToTop />
      <ConfigProvider theme={{ token: { fontFamily: "'Lato', sans-serif" } }}>
        <Routes>
          {/* Default route - detect language and redirect */}
          <Route path='/' element={<LanguageDetector />} />

          {/* Multilingual routes generated programmatically */}
          {LANGS.map((lang) => (
            <>
              <Route key={`${lang}-home`} path={`/${lang}`} element={<MainPage />} />
              {commonContentRoutes.map((r) => (
                <Route key={`${lang}-${r.path}`} path={`/${lang}/${r.path}`} element={r.element} />
              ))}
              <Route
                key={`${lang}-booking`}
                path={`/${lang}/booking/:token`}
                element={
                  <PrivateRoutesClient>
                    <Booking />
                  </PrivateRoutesClient>
                }
              />
              {authRoutes.map((r) => (
                <Route key={`${lang}-${r.path}`} path={`/${lang}/${r.path}`} element={r.element} />
              ))}
            </>
          ))}

          {/* Public sign-in/sign-up routes (root level - fallback) */}
          {authRoutes.map((r) => (
            <Route key={`root-${r.path}`} path={`/${r.path}`} element={r.element} />
          ))}

          {/* Protected admin routes */}
          <Route path='/admin/*' element={<LayoutRoutesAdmin routes={adminRoutes} />} />
          {LANGS.map((lang) => (
            <Route key={`${lang}-admin`} path={`/${lang}/admin/*`} element={<LayoutRoutesAdmin routes={adminRoutes} />} />
          ))}

          {/* Protected consultant routes */}
          <Route
            path='/consultant/*'
            element={<LayoutRoutesConsultant routes={consulatntRoutes} />}
          />
          {LANGS.map((lang) => (
            <Route key={`${lang}-consultant`} path={`/${lang}/consultant/*`} element={<LayoutRoutesConsultant routes={consulatntRoutes} />} />
          ))}

          {/* Protected client routes */}
          {/* <Route path='/client/*' element={<LayoutRoutesClient routes={clientRoutes} />} /> */}
          {LANGS.map((lang) => (
            <Route path={`/${lang}/client/*`} element={<LayoutRoutesClient routes={clientRoutes} />} />
          ))}

          {/* Fallback for any other routes */}
          <Route path='*' element={<NotFound />} />
        </Routes>
      </ConfigProvider>
    </LanguageProvider>
  );
};

// Language Detector Component
const LanguageDetector = () => {
  const { type: currentLanguage } = useSelector((state: AppState) => state.language);

  // Detect browser language if Redux state is not set
  const detectBrowserLanguage = (): 'en' | 'fr' => {
    const browserLang = navigator.language || navigator.languages[0] || 'en';
    return browserLang.toLowerCase().startsWith('fr') ? 'fr' : 'en';
  };

  const language = currentLanguage || detectBrowserLanguage();
  const targetPath = language === 'fr' ? '/fr' : '/en';

  return <Navigate to={targetPath} replace />;
};

// Protected Route Components with proper structure
const LayoutRoutesAdmin = ({ routes }) => (
  <AdminLayout>
    <Routes>
      {routes.map((route, index) => (
        <Route
          key={index}
          path={route.path}
          element={
            <PrivateRoutesAdmin>
              <route.component />
            </PrivateRoutesAdmin>
          }
        />
      ))}
      <Route path='*' element={<Navigate to='dashboard' replace />} />
    </Routes>
  </AdminLayout>
);

const LayoutRoutesClient = ({ routes }) => (
  <ClientLayout>
    <Routes>
      {routes.map((route, index) => (
        <Route
          key={index}
          path={route.path}
          element={
            <PrivateRoutesClient>
              <route.component />
            </PrivateRoutesClient>
          }
        />
      ))}
      <Route path='*' element={<Navigate to='dashboard' replace />} />
    </Routes>
  </ClientLayout>
);

const LayoutRoutesConsultant = ({ routes }) => (
  <ConsultantLayout>
    <Routes>
      {routes.map((route, index) => (
        <Route
          key={index}
          path={route.path}
          element={
            <PrivateRoutesCounsultant>
              <route.component />
            </PrivateRoutesCounsultant>
          }
        />
      ))}
      <Route path='*' element={<Navigate to='dashboard' replace />} />
    </Routes>
  </ConsultantLayout>
);

export default App;
